import './Quotes.css';
import { PieChart, Pie, Tooltip, Legend, Cell, Customized } from 'recharts';
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { generateQuote } from "./QuoteGenerator";
import moment from "moment";
import { getInterestRateForProjections, depositModeNameForPlan } from "../Utils/ConfigUtil";
import {
    beneficiaryCalendarYearsAge,
    beneficiaryCalendarYearsAgeAtMaturity,
    roundHalfUp,
} from "../Utils/PlanUtils";
import { formatCurrency, formatPercentage, formatUnits } from "../Internationalization/Formatters";
import {
    getNetSavingsForQuote,
    getTotalGrantForQuote,
    getTotalGrantInterestForQuote,
    pieChartMinimumPercentForLabel
} from "../Utils/ProjectionUtils";

import { sprintf } from "../Utils/FormattingUtils";

const COLORS = ['RGB(131.325,204,255)', 'RGB(191.25,224.4,204)', 'RGB(114.75,216.75,63.75)', 'RGB(255,206.55,130.05)'];


//If setQuoteImage is set, a PNG image of the rendered component will be sent to the function
// after the component has finished mounting.  Intended to generate PDF from the component.
// If fullScale is set, the component will be displayed at full scale, otherwise it will be scaled down to fit on a page
const SelfInitiatedQuote = ({ state, setQuoteImage, page, totalPages, fullScale }) => {
    const componentRef = useRef(); // Reference to the component
    const { t, i18n } = useTranslation();
    const [quoteAttributes, setQuoteAttributes] = useState(null);
    const [pieChartData, setPieChartData] = useState([]);
    const [totalPie, setTotalPie] = useState(0);

    //Once we have all of the data we need for the quote, we assume it is displayed and
    // generate the image for the quote
    useEffect(() => {
        if (!setQuoteImage || !componentRef.current || pieChartData.length === 0) {
            return;
        }

        html2canvas(componentRef.current).then((canvas) => {
            setQuoteImage(canvas.toDataURL('image/png'));
        });

    }, [setQuoteImage, quoteAttributes, componentRef.current]);

    useEffect(() => {
        setQuoteAttributes(generateQuote(state));
    }, [state]);

    useEffect(() => {
        if (!quoteAttributes) {
            return;
        }

        let data = [];

        const grant = roundHalfUp(getTotalGrantForQuote(quoteAttributes), 0);
        const grantIncome = roundHalfUp(getTotalGrantInterestForQuote(quoteAttributes), 0);
        const EAPs = roundHalfUp(quoteAttributes.EAPAmount, 0);
        const netSavings = roundHalfUp(getNetSavingsForQuote(quoteAttributes), 0);
        if (grant !== 0) {
            data.push({ name: t('Grant'), value: grant });
        }
        if (grantIncome !== 0) {
            data.push({ name: t('Grant Income'), value: grantIncome });
        }
        if (EAPs !== 0) {
            data.push({ name: t('EAPs'), value: EAPs });
        }
        if (netSavings !== 0) {
            data.push({ name: t('Net Savings'), value: netSavings });
        }


        setPieChartData(data);

        setTotalPie(data.reduce((acc, cur) => acc + cur.value, 0));

    }, [quoteAttributes]);

    //Displays a currency value in the format of the current locale
    const displayCurrency = (value, decimals = 0) => {
        return formatCurrency(roundHalfUp(value, decimals), i18n.language, decimals);
    }

    //Displays a date in the format MMM D, YYYY
    // If the date is invalid, an empty string is returned
    const displayDate = (value) => {
        const ret = moment(value).format('MMM D, YYYY');
        if (ret === 'Invalid date') { return ''; }
        return ret;
    }


    //Draw labels for the pie graph inside the pie pieces
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * (1 - percent) * 0.8;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        //if (percent > pieChartMinimumPercentForLabel) {
        return (
            <svg>
                <text x={x} y={y + 10} fill="#111" textAnchor={x > cx ? 'middle' : 'middle'} dominantBaseline="central">
                    {displayCurrency(value)}
                </text>

                <text x={x} y={y - 10} fill="#111" textAnchor={x > cx ? 'middle' : 'middle'} dominantBaseline="central">
                    {name}
                </text>
            </svg>
        )
        /*} else {
            //If the piece of the pie is too small to display the label, we write it at the bottom left
            //This only works if one piece is this small...
            return (
                <svg>
                    <text x={0} y={cy * 2 - 20} fill="#111" textAnchor={'left'} dominantBaseline="central" fontSize={13}>
                        {displayCurrency(value)}
                    </text>

                    <text x={0} y={cy * 2 - 40} fill="#111" textAnchor={'left'} dominantBaseline="central" fontSize={13}>
                        {name}
                    </text>
                </svg>
            )
        }*/

    };

    const pageCountLabel = () => {
        if (!page || !totalPages) {
            return '';
        }
        return sprintf(t('x of y'), page, totalPages);
    }


    if (!quoteAttributes || !pieChartData) {
        return (
            <div>Generating quote...</div>
        )
    } else {
        return (
            <div ref={componentRef} className={`quote ${fullScale ? 'full-scale' : ''}`}>
                <div className="quote--header">
                    <div className="quote--header__left">
                        <h1>{t('Children\'s Education Funds Inc.')}</h1>
                        <h2>{t('RESP Quotation')}</h2>
                        <h2>{t('Self-Initiated Option Plan')}</h2>
                        <h3>{t('For illustration purposes only')}</h3>
                    </div>
                    <div className={"quote--header__right"}>
                        <h1>{t(depositModeNameForPlan(state))}</h1>
                        <h2>{pageCountLabel()}</h2>
                    </div>
                </div>

                <div className={'visualizations'}>
                    <div className={'visualizations__left'}>
                        <h1>{t('Breakdown of your Self-Initiated Plan (Estimate)')}</h1>
                        <div className={'pie-chart-container'}>
                            <PieChart width={400} height={400}>
                                <Pie
                                    dataKey="value"
                                    isAnimationActive={false}
                                    data={pieChartData}
                                    cx={180}
                                    cy={180}
                                    outerRadius={150}
                                    fill="#8884d8"
                                    labelLine={false}
                                    label={renderCustomizedLabel}
                                    stroke={false}
                                >
                                    {
                                        pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))
                                    }
                                </Pie>
                                <Tooltip formatter={(value, name, props) => {
                                    return [displayCurrency(value), name];
                                }} />
                            </PieChart>
                            <div className={'pie-chart-overlay'}></div>
                        </div>
                        <div className='pie-chart-center-label'>
                            {displayCurrency(totalPie, 0)}
                        </div>
                    </div>
                    <div className={'visualizations__right self-initiated'}>
                        <div className={'info'}>
                            {t('Please read page 2 for the assumptions and important information used in preparing this illustration.')}
                        </div>
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        {/* <div className={'airmiles-plus'}>{t('PLUS')}</div> */}
                                        {
                                            //TODO: Translation for the below
                                        }
                                        {/* <div>
                                            {sprintf(t("Your contributions will earn you %i AIR MILES Reward Miles over the life of the plan!"), Math.trunc(quoteAttributes.airMilesEarned))}
                                        </div> */}
                                    </td>
                                    <td>
                                        {/* <img width={100} height={100} src={'/images/new_amlogo-2-2.png'}
                                            alt={t('AIR Miles logo')} /> */}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={'details-section'}>
                    <h1>{t("Plan Options")}</h1>
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Mode')}</span>
                                    <span className={'value'}>{state.plan.depositMode}-{state.plan.depositCode}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Number of Units')}</span>
                                    <span className={'value'}>{formatUnits(state.plan.numberOfUnits)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Effective Date')}</span>
                                    <span className={'value'}>{displayDate(state.plan.effectiveDate)}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className={'details-section'}>
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    <h1>{t('Contributions')}</h1>
                                </th>
                                <th>
                                    <h1>{t('Government Grants')}</h1>
                                </th>
                                <th>
                                    <h1>{t('Plan at Maturity')}</h1>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Contribution Amount')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.amountPerDeposit, 2)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('CESG Grant')}</span>
                                    <span
                                        className={'value'}>{displayCurrency(quoteAttributes.CESGAmount)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Maturity Date')}</span>
                                    <span className={'value'}>{displayDate(state.plan.maturityDate)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Initial Contribution')}</span>
                                    <span
                                        className={'value'}>{displayCurrency(quoteAttributes.initialDepositAmount, 2)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('CESG Grant Income')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.CESGInterest)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('EAP')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.EAPAmount)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Yearly Contribution Amount')}</span>
                                    {state.plan.depositMode !== 'T' &&
                                        <span className={'value'}>
                                            {displayCurrency(quoteAttributes.yearlyDepositAmount, 2)}
                                        </span>
                                    }
                                    {state.plan.depositMode === 'T' &&
                                        <span className={'value'}>
                                            N/A
                                        </span>
                                    }
                                </td>
                                <td>
                                    <span className={'label'}>{t('A-CESG Grant')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.ACESGAmount)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Total Contributions')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.totalDeposits)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Amount Required on Next Contribution')}</span>
                                    {state.plan.depositMode !== 'T' &&
                                        <span className={'value'}>
                                            {displayCurrency(quoteAttributes.amountRequiredOnNextDeposit, 2)}
                                        </span>
                                    }
                                    {state.plan.depositMode === 'T' &&
                                        <span className={'value'}>
                                            N/A
                                        </span>
                                    }
                                </td>
                                <td>
                                    <span className={'label'}>{t('A-CESG Grant Income')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.ACESGInterest)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Charge')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.enrolmentFee)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Next Contribution Date')}</span>
                                    {state.plan.depositMode !== 'T' &&
                                        <span className={'value'}>
                                            {displayDate(quoteAttributes.nextDepositDate)}
                                        </span>
                                    }
                                    {state.plan.depositMode === 'T' &&
                                        <span className={'value'}>
                                            N/A
                                        </span>
                                    }
                                </td>
                                <td>
                                    <span className={'label'}>{t('CLB Grant')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.CLBAmount)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Depository Fees')}<sup>1</sup></span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.depositoryFee)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Number of Additional Contributions Required')}</span>
                                    {state.plan.depositMode !== 'T' &&
                                        <span className={'value'}>
                                            {quoteAttributes.numberOfAdditionalDeposits}
                                        </span>
                                    }
                                    {state.plan.depositMode === 'T' &&
                                        <span className={'value'}>
                                            N/A
                                        </span>
                                    }
                                </td>
                                <td>
                                    <span className={'label'}>{t('CLB Grant Income')}</span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.CLBInterest)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Total Net Contributions')}</span>
                                    <span className={'value'}>{displayCurrency(getNetSavingsForQuote(quoteAttributes))}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Last Contribution Date')}</span>
                                    <span className={'value'}>{displayDate(quoteAttributes.finalDepositDate)}</span>
                                </td>
                                <td>
                                    {state.common.signingProvince === 'PQ' &&
                                        <div>
                                            <span className={'label'}>{t('QESI')}</span>
                                            <span
                                                className={'value'}>{displayCurrency(quoteAttributes.QESIAmount + quoteAttributes.AQESIAmount)}</span>
                                        </div>
                                    }
                                </td>
                                <td>
                                    <span className={'label'}>{t('Insurance Premiums')}<sup>2</sup></span>
                                    <span className={'value'}>{displayCurrency(quoteAttributes.totalInsuranceAmount)}</span>
                                </td>
                            </tr>
                            {state.common.signingProvince === 'PQ' &&
                                <tr>
                                    <td></td>
                                    <td>
                                        <span className={'label'}>{t('QESI Income')}</span>
                                        <span className={'value'}>{displayCurrency(quoteAttributes.QESIInterest + quoteAttributes.AQESIInterest)}</span>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>

                <div className={'details-section'}>
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    <h1>{t('Quotation Details')}</h1>
                                </th>
                                <th>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Date of Presentation')}</span>
                                    <span className={'value'}>{displayDate(moment())}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Estimated Starting Studies in Year')}</span>
                                    <span className={'value'}>{moment(state.plan.maturityDate).year()}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Prepared By')}</span>
                                    <span className={'value'}>{state.agent.firstName} {state.agent.lastName}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Age of Beneficiary in Maturity Year')}</span>
                                    <span className={'value'}>{beneficiaryCalendarYearsAgeAtMaturity(state)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Prepared For')}</span>
                                    <span
                                        className={'value'}>{state.subscriber.firstName} {state.subscriber.lastName}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Projection Interest Rate')}</span>
                                    <span
                                        className={'value'}>{formatPercentage(getInterestRateForProjections(), i18n.language)}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Province')}</span>
                                    <span className={'value'}>{state.common.signingProvince}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Completion Insurance')}</span>
                                    <span className={'value'}>{state.plan.completionInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Family Income')}</span>
                                    <span className={'value'}>{displayCurrency(state.knowYourClient.familyIncome)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Insurance')}</span>
                                    <span className={'value'}>{state.plan.nomineeInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Name')}</span>
                                    <span
                                        className={'value'}>{state.beneficiary.firstName} {state.beneficiary.lastName}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Payment Protection Insurance')}</span>
                                    <span className={'value'}>{state.plan.paymentProtectionInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Birth Date')}</span>
                                    <span className={'value'}>{displayDate(state.beneficiary.birthdate)}</span>
                                </td>
                                <td>
                                    <span className={'label'}>{t('Critical Illness Insurance')}</span>
                                    <span className={'value'}>{state.plan.criticalIllnessInsurance ? t("YES") : t("NO")}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span className={'label'}>{t('Beneficiary Age')}</span>
                                    <span
                                        className={'value'}>{beneficiaryCalendarYearsAge(state.beneficiary.birthdate)}</span>
                                </td>
                                <td>
                                    <table>
                                        <tr>
                                            <td>
                                                <span className={'label'}>{t('CESG')}</span>
                                                <span
                                                    className={'value'}>{state.application.grantCanadaEducationSavings ? t("YES") : t("NO")}</span>
                                            </td>
                                            <td>
                                                <div>
                                                    <span className={'label'}>
                                                        {state.common.signingProvince === 'PQ' &&
                                                            <span>{t('QESI')}</span>
                                                        }
                                                        {state.common.signingProvince === 'BC' &&
                                                            <span>{t('BCTESG')}</span>
                                                        }
                                                    </span>
                                                    {(state.common.signingProvince === 'PQ' || state.common.signingProvince === 'BC') &&
                                                        <span className={'value'}>
                                                            {state.application.grantProvincial ? t("YES") : t("NO")}
                                                        </span>
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                <span className={'label'}>{t('CLB')}</span>
                                                <span
                                                    className={'value'}>{state.application.grantCanadaLearningBond ? t("YES") : t("NO")}</span>
                                            </td>
                                        </tr>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className={'footer'}>
                </div>


                <div>
                    {
                        //JSON.stringify(generateQuote(state, true), null, 2)
                    }
                </div>
            </div>
        )
    }
}

export default SelfInitiatedQuote;